// F-082 — Boîte de réception documentaire : dépôt, proposition de classement, confirmation et rattachement.
import { useState } from "react";
import { post, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, fileSize } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Empty, ErrorBox, FileDrop, Input, Loading, Modal, MoneyInput, Notice, PageHeader, Select, SimBadge, Source, Tabs, Textarea, fieldError } from "@core/ui";
import type { Entry } from "./operations";
import { Amount, BuildingSelect, DocLink, LeaseSelect, PropertySelect, RECOVERABLE, currentYear, isConflict, useAction, useCatalog, useRefs } from "./shared";

type Suggestion = {
  source: "aucune" | "lecture_automatique";
  doc_type?: string | null;
  doc_date?: string | null;
  amount_cents?: number | null;
  issuer?: string | null;
  reference?: string | null;
  property_id?: string | null;
  property_reason?: string | null;
  category?: string | null;
  summary?: string | null;
};

type Item = {
  id: string;
  status: "a_traiter" | "classe" | "ignore";
  channel: string;
  sender: string;
  document_id: string;
  title: string;
  filename: string;
  mime: string;
  size_bytes: number;
  document_category: string;
  duplicate_of_document_id: string | null;
  duplicate_of_title: string | null;
  extraction: { available?: boolean; mode?: string; provider?: string; note?: string | null };
  extraction_mode: string;
  suggestion: Suggestion;
  doc_type: string | null;
  doc_date: string | null;
  amount_cents: number | null;
  issuer: string | null;
  property_id: string | null;
  property_label: string | null;
  lease_id: string | null;
  operation_id: string | null;
  operation_label: string | null;
  ignore_reason: string;
  classified_at: string | null;
  created_at: string;
};

type Status = "a_traiter" | "classe" | "ignore" | "tout";

export function InboxPage() {
  const org = useOrg();
  const [status, setStatus] = useState<Status>("a_traiter");
  const q = useGet<{ items: Item[]; pending: number; reader: { name: string; mode: string }; email_channel: { configured: boolean; note: string } }>(org.api(`/finance/inbox?status=${status}`));
  const [classifying, setClassifying] = useState<Item | null>(null);
  const [ignoring, setIgnoring] = useState<Item | null>(null);
  const a = useAction();
  const canDrop = org.can("operate") || org.can("finance");
  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Boîte de réception"
        intro="Déposez factures, avis, appels de fonds, courriers ou contrats. Bailivo propose un classement ; vous le confirmez ou le corrigez avant tout rattachement."
      />
      {q.data ? (
        <Notice tone={q.data.reader.mode === "production" ? "info" : "simulateur"} title={<>Lecture des pièces <SimBadge mode={q.data.reader.mode} /></>}>
          {q.data.reader.mode === "production"
            ? "Les champs lus automatiquement sont des propositions à confirmer : ils sont surlignés et renvoient à la pièce."
            : "Lecture automatique non configurée : aucun champ n’est lu dans la pièce. Vous saisissez le classement vous-même."}{" "}
          {q.data.email_channel.note}
        </Notice>
      ) : null}
      {canDrop ? (
        <FileDrop
          multiple
          busy={a.busy}
          label="Déposer des pièces"
          onFiles={async (files) => {
            for (const file of files) {
              const form = new FormData();
              form.append("file", file);
              await a.run(() => upload(org.api("/finance/inbox"), form), { refresh: [org.api("/finance/inbox")] });
            }
          }}
        />
      ) : null}
      <ErrorBox error={a.error} />
      <Tabs
        value={status}
        onChange={setStatus}
        items={[
          { value: "a_traiter", label: `À traiter${q.data && status === "a_traiter" ? ` (${q.data.pending})` : ""}` },
          { value: "classe", label: "Classées" },
          { value: "ignore", label: "Écartées" },
          { value: "tout", label: "Toutes" },
        ]}
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title={status === "a_traiter" ? "Aucune pièce à traiter." : "Aucune pièce."}>Les pièces déposées apparaissent ici jusqu’à leur classement.</Empty> : null}
      <div className="stack">
        {(q.data?.items ?? []).map((it) => (
          <article key={it.id} className="finances-element">
            <div className="finances-element-tete">
              <div>
                <h3>
                  <DocLink id={it.document_id} name={it.title || it.filename} />
                </h3>
                <p className="small muted">
                  {it.filename} · {fileSize(it.size_bytes)} · déposée le {dateTime(it.created_at)}
                  {it.sender ? ` par ${it.sender}` : ""}
                </p>
              </div>
              <div className="row">
                {it.status === "classe" ? <Badge tone="succes">Classée</Badge> : it.status === "ignore" ? <Badge>Écartée</Badge> : <Badge tone="encre">À traiter</Badge>}
                {it.duplicate_of_document_id ? <Badge tone="attention">Doublon possible</Badge> : null}
              </div>
            </div>
            {it.duplicate_of_document_id ? (
              <p className="small">
                Même fichier que <DocLink id={it.duplicate_of_document_id} name={it.duplicate_of_title || "une pièce déjà reçue"} /> : vérifiez avant de l’enregistrer une seconde fois.
              </p>
            ) : null}
            {it.status === "classe" ? (
              <p className="small">
                {[it.doc_type, it.doc_date ? date(it.doc_date) : null, it.issuer, it.property_label].filter(Boolean).join(" · ")}
                {it.amount_cents ? <> · <Amount cents={it.amount_cents} /></> : null}
                {it.operation_label ? ` · opération « ${it.operation_label} »` : ""}
                {it.lease_id ? " · rattachée au bail (visible par le locataire)" : ""}
              </p>
            ) : null}
            {it.status === "ignore" && it.ignore_reason ? <p className="small muted">Motif : {it.ignore_reason}</p> : null}
            {it.status === "a_traiter" && it.suggestion.source === "lecture_automatique" ? (
              <p className="small">
                Proposition : <span className="finances-propose">{[it.suggestion.doc_type, it.suggestion.issuer, it.suggestion.doc_date ? date(it.suggestion.doc_date) : null].filter(Boolean).join(" · ") || "aucun champ lu"}</span>{" "}
                <Source doc={it.filename} details={["lecture automatique", "à confirmer"]} extrait />
              </p>
            ) : null}
            {canDrop && it.status !== "ignore" ? (
              <div className="finances-actions">
                {it.status === "a_traiter" ? (
                  <Button variant="discret" small onClick={() => setIgnoring(it)}>
                    Écarter
                  </Button>
                ) : null}
                <Button small variant={it.status === "a_traiter" ? "primaire" : "secondaire"} onClick={() => setClassifying(it)}>
                  {it.status === "a_traiter" ? "Classer" : "Corriger le classement"}
                </Button>
              </div>
            ) : null}
          </article>
        ))}
      </div>
      {classifying ? <ClassifyModal item={classifying} onClose={() => setClassifying(null)} /> : null}
      {ignoring ? <IgnoreModal item={ignoring} onClose={() => setIgnoring(null)} /> : null}
    </div>
  );
}

function IgnoreModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const org = useOrg();
  const [reason, setReason] = useState(item.duplicate_of_document_id ? "Doublon d’une pièce déjà reçue" : "");
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Écarter la pièce"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const r = await a.run(() => post(org.api(`/finance/inbox/${item.id}/ignore`), { reason }), { success: "Pièce écartée.", refresh: [org.api("/finance/inbox")] });
              if (r) onClose();
            }}
          >
            Écarter
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>La pièce reste conservée avec sa provenance ; elle n’est rattachée à rien.</p>
        <Textarea label="Motif" value={reason} onChange={(e) => setReason(e.target.value)} />
        <ErrorBox error={a.error} />
      </div>
    </Modal>
  );
}

function ClassifyModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const catalog = useCatalog();
  const s = item.suggestion;
  const auto = s.source === "lecture_automatique";
  const [f, setF] = useState({
    doc_type: item.doc_type ?? s.doc_type ?? "",
    title: item.title,
    doc_date: item.doc_date ?? s.doc_date ?? "",
    amount_cents: item.amount_cents ?? s.amount_cents ?? null,
    issuer: item.issuer ?? s.issuer ?? "",
    property_id: item.property_id ?? s.property_id ?? "",
    building_id: "",
    lease_id: item.lease_id ?? "",
  });
  const [mode, setMode] = useState<"aucune" | "creer" | "rattacher">(item.operation_id ? "aucune" : "creer");
  const [op, setOp] = useState({ kind: "depense", category: s.category ?? "", recoverable: "non", recoverable_cents: null as number | null, link: "" });
  const canFinance = org.can("finance");
  const candidates = useGet<{ items: Entry[] }>(mode === "rattacher" ? org.api(`/finance/operations?year=${currentYear()}&missing_document=true`) : null);
  const a = useAction();
  const cats = (catalog.data?.categories ?? []).filter((c) => c.kind === op.kind && c.manual);
  const docTypes = catalog.data?.doc_types ?? [];
  const proposed = (field: keyof Suggestion, value: unknown) => auto && s[field] !== undefined && s[field] !== null && s[field] === value;
  const help = (field: keyof Suggestion, value: unknown) => (proposed(field, value) ? <span className="finances-propose">Proposé par lecture automatique : à confirmer</span> : undefined);
  const submit = async (confirmDuplicate: boolean) => {
    const body: Record<string, unknown> = {
      doc_type: f.doc_type,
      title: f.title,
      doc_date: f.doc_date || null,
      amount_cents: f.amount_cents,
      issuer: f.issuer,
      property_id: f.property_id || null,
      building_id: f.property_id ? null : f.building_id || null,
      lease_id: f.lease_id || null,
    };
    if (canFinance && !item.operation_id && mode === "creer") {
      body.operation = { kind: op.kind, category: op.category, recoverable: op.kind === "depense" ? op.recoverable : "non", recoverable_cents: op.recoverable_cents ?? 0, confirm_duplicate: confirmDuplicate };
    }
    if (canFinance && !item.operation_id && mode === "rattacher" && op.link) body.link_operation_id = op.link;
    const r = await a.run(() => post(org.api(`/finance/inbox/${item.id}/classify`), body), { success: "Pièce classée.", refresh: [org.api("/finance")] });
    if (r) onClose();
  };
  const duplicate = isConflict(a.error) ? (a.error as Error).message : null;
  return (
    <Modal
      open
      onClose={onClose}
      title="Classer la pièce"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          {duplicate && mode === "creer" ? (
            <Button busy={a.busy} onClick={() => submit(true)}>Confirmer malgré tout</Button>
          ) : (
            <Button busy={a.busy} onClick={() => submit(false)}>Confirmer le classement</Button>
          )}
        </>
      }
    >
      <div className="stack">
        <p>
          <DocLink id={item.document_id} name={`Ouvrir ${item.filename}`} /> dans un nouvel onglet pour vérifier chaque valeur.
        </p>
        {!auto ? <Notice tone="simulateur">{item.extraction.note ?? "Aucune lecture automatique : saisissez les informations de la pièce."}</Notice> : null}
        {duplicate ? <Notice tone="attention" title="À vérifier">{duplicate}</Notice> : <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />}
        <div className="form-row">
          <Select label="Type de pièce" required value={f.doc_type} help={help("doc_type", f.doc_type)} error={fieldError(a.error, "doc_type")} onChange={(e) => setF({ ...f, doc_type: e.target.value })} options={[{ value: "", label: "Choisir…" }, ...docTypes.map((d) => ({ value: d.code, label: d.label }))]} />
          <Input label="Date de la pièce" type="date" value={f.doc_date} help={help("doc_date", f.doc_date)} error={fieldError(a.error, "doc_date")} onChange={(e) => setF({ ...f, doc_date: e.target.value })} />
        </div>
        <div className="form-row">
          <MoneyInput label="Montant" cents={f.amount_cents} help={help("amount_cents", f.amount_cents)} error={fieldError(a.error, "amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
          <Input label="Émetteur" value={f.issuer} help={help("issuer", f.issuer)} onChange={(e) => setF({ ...f, issuer: e.target.value })} />
        </div>
        <Input label="Titre dans la bibliothèque" value={f.title} placeholder="Ex. Facture plomberie juin" onChange={(e) => setF({ ...f, title: e.target.value })} />
        <div className="form-row">
          <PropertySelect refs={refs.data} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v, lease_id: "" })} empty="Aucun logement" />
          {f.property_id ? (
            <LeaseSelect
              refs={refs.data}
              propertyId={f.property_id}
              value={f.lease_id}
              onChange={(v) => setF({ ...f, lease_id: v })}
              empty="Ne pas rattacher au bail"
              help="Une pièce rattachée au bail devient consultable par ses locataires."
            />
          ) : (
            <BuildingSelect refs={refs.data} value={f.building_id} onChange={(v) => setF({ ...f, building_id: v })} />
          )}
        </div>
        {s.property_reason && proposed("property_id", f.property_id) ? <p className="small"><span className="finances-propose">{s.property_reason}</span> : proposition à confirmer.</p> : null}
        {canFinance && !item.operation_id ? (
          <fieldset className="stack-sm" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="small"><strong>Opération financière</strong></legend>
            <Select
              label="Cette pièce justifie"
              value={mode}
              onChange={(e) => setMode(e.target.value as typeof mode)}
              options={[
                { value: "creer", label: "Une nouvelle opération (recette ou dépense)" },
                { value: "rattacher", label: "Une opération déjà enregistrée sans justificatif" },
                { value: "aucune", label: "Aucune opération (pièce à classer seulement)" },
              ]}
            />
            {mode === "creer" ? (
              <div className="form-row">
                <Select label="Type" value={op.kind} onChange={(e) => setOp({ ...op, kind: e.target.value, category: "" })} options={[{ value: "depense", label: "Dépense" }, { value: "recette", label: "Recette" }]} />
                <Select label="Catégorie" required value={op.category} help={help("category", op.category)} error={fieldError(a.error, "category")} onChange={(e) => setOp({ ...op, category: e.target.value })} options={[{ value: "", label: "Choisir…" }, ...cats.map((c) => ({ value: c.code, label: c.label }))]} />
                {op.kind === "depense" ? (
                  <Select label="Récupérable" value={op.recoverable} onChange={(e) => setOp({ ...op, recoverable: e.target.value })} options={Object.entries(RECOVERABLE).map(([value, label]) => ({ value, label }))} />
                ) : null}
                {op.kind === "depense" && op.recoverable === "partiel" ? <MoneyInput label="Part récupérable" cents={op.recoverable_cents} error={fieldError(a.error, "recoverable_cents")} onChange={(c) => setOp({ ...op, recoverable_cents: c })} /> : null}
              </div>
            ) : null}
            {mode === "rattacher" ? (
              <Select
                label="Opération"
                value={op.link}
                onChange={(e) => setOp({ ...op, link: e.target.value })}
                options={[{ value: "", label: candidates.isLoading ? "Chargement…" : "Choisir…" }, ...(candidates.data?.items ?? []).filter((x) => x.origin === "operation").map((x) => ({ value: x.id, label: `${date(x.date)} — ${x.label} (${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(x.amount_cents / 100)})` }))]}
              />
            ) : null}
          </fieldset>
        ) : item.operation_id ? (
          <p className="small muted">Cette pièce justifie déjà l’opération « {item.operation_label} ».</p>
        ) : (
          <p className="small muted">Votre rôle ne permet pas de créer d’opération financière : la pièce sera seulement classée.</p>
        )}
      </div>
    </Modal>
  );
}
